import axios from 'axios'
import type { AxiosError, AxiosResponse } from 'axios'

import { isAdminTenantCodeMode } from '@/apps/admin/config/tenant-mode'
import { resolveAdminApiLocale } from '@/apps/admin/i18n/locale'
import { readAdminTenantCode } from '@/apps/admin/utils/tenant-preference'
import { clearAdminSession, readAdminSession } from '@/stores/admin/session-storage'
import type { ApiResponse } from '@/shared/types/http'

type AdminUnauthorizedHandler = () => void

let unauthorizedHandler: AdminUnauthorizedHandler | null = null

export class AdminApiError extends Error {
  readonly code: number | string

  constructor(message: string, code: number | string) {
    super(message)
    this.name = 'AdminApiError'
    this.code = code
  }
}

export const setAdminUnauthorizedHandler = (
  handler: AdminUnauthorizedHandler | null
): void => {
  unauthorizedHandler = handler
}

const handleUnauthorized = (): void => {
  clearAdminSession()
  unauthorizedHandler?.()
}

export const adminClient = axios.create({
  baseURL: import.meta.env.VITE_ADMIN_API_BASE_URL,
  timeout: 180000,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
})

adminClient.interceptors.request.use(config => {
  const { token } = readAdminSession()

  if (config.params && typeof config.params === 'object' &&
    Object.keys(config.params).some(key => key.toLowerCase() === 'token')) {
    throw new Error('Admin token 禁止进入 URL 查询参数')
  }

  if (token) {
    config.headers.set('token', token)
  }
  if (isAdminTenantCodeMode()) {
    config.headers.set('Tenant-Code', readAdminTenantCode())
  } else {
    config.headers.delete('Tenant-Code')
  }

  if (config.method?.toLowerCase() === 'get') {
    config.params = { ...(config.params || {}), _t: Date.now() }
  }

  if (typeof FormData !== 'undefined' && config.data instanceof FormData &&
    config.headers.get('Content-Type') === 'application/json') {
    config.headers.delete('Content-Type')
  }

  config.headers.set('Accept-Language', resolveAdminApiLocale())
  config.headers.set('X-Requested-With', 'XMLHttpRequest')

  return config
})

adminClient.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    // 导出接口返回二进制响应，业务错误由下载层读取 JSON Blob 后统一转换。
    if (response.config.responseType === 'blob') {
      return response
    }

    const { code, msg } = response.data

    if (code === 0 || code === '0') {
      return response
    }

    if (code === 401 || code === '401' || code === 10001 || code === '10001') {
      handleUnauthorized()
    }

    return Promise.reject(new AdminApiError(msg || 'Admin 请求失败', code))
  },
  (error: AxiosError<ApiResponse>) => {
    if (error.response?.status === 401) {
      handleUnauthorized()
    }

    return Promise.reject(error)
  }
)
